/**
 * On Today: a paper a teacher has set that the student has not started.
 *
 * The notice a student was missing — papers were only ever listed under
 * Tests → Assigned, so a teacher could set one and the child would land on
 * Today every morning and never hear of it. It leads with the newest paper and
 * starts it in one press; anything else waiting is a link to the full list.
 * Renders nothing once every paper has been started, which is what clears it.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { apiPost } from '@/lib/api';
import { useAssignedPapers } from '@/hooks/useAssignedPapers';
import { Button } from '@/components/ds';
import { Icon } from '@/components/garden';

export function AssignedPapersBanner() {
  const navigate = useNavigate();
  const { unstarted } = useAssignedPapers();
  const [failed, setFailed] = useState(false);

  const start = useMutation({
    mutationFn: (paperId: string) => apiPost<{ id: string }>('/student-papers', { paperId }),
    onSuccess: (session) => navigate(`/exam/${session.id}`),
    onError: () => setFailed(true),
  });

  const latest = unstarted[0];
  if (!latest) return null;
  const minutes = Math.max(1, Math.round(latest.totalSeconds / 60));
  const more = unstarted.length - 1;

  return (
    <section
      aria-label="New paper from your teacher"
      className="flex flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3"
      style={{ background: 'var(--brand-lime-soft)', border: '1px solid var(--brand-ink)' }}
    >
      <div className="min-w-0 text-sm" style={{ color: 'var(--brand-ink)' }}>
        <p>
          <strong>New paper from your teacher</strong> · {latest.subjectName}
        </p>
        <p className="mt-0.5 truncate">
          {latest.title} ·{' '}
          <span data-numeric="">
            {latest.questionCount} question{latest.questionCount === 1 ? '' : 's'} · about {minutes}{' '}
            min
          </span>
          {more > 0 && (
            <>
              {' · '}
              <Link to="/tests/assigned" className="font-semibold">
                {more} more waiting
              </Link>
            </>
          )}
        </p>
        {failed && (
          <p className="mt-0.5 text-xs text-danger">Couldn’t start that paper. Please try again.</p>
        )}
      </div>
      <Button
        size="sm"
        disabled={start.isPending}
        onClick={() => {
          setFailed(false);
          start.mutate(latest.id);
        }}
        iconLeft={<Icon name="play" size={13} />}
      >
        {start.isPending ? 'Starting…' : 'Start'}
      </Button>
    </section>
  );
}
